/**
 * Purple Range — continuous attack/defend simulation.
 *
 * Three cooperating agents run breach-and-attack-simulation (BAS) over the
 * organisation's OWN modeled estate: RED plans kill-chains from the toxic-combo
 * paths + losing exploitation races, GREEN judges each step against current
 * detection coverage + compensating controls, PURPLE referees + scores + files
 * unmitigated gaps to the Phase-A review queue.
 *
 * SIMULATION OVER THE MODELED ESTATE — no live exploitation. Nothing is
 * executed against real systems; gaps go to the human-review queue, never
 * auto-applied. Same legitimate defensive category as SafeBreach / AttackIQ /
 * Cymulate / MITRE Caldera.
 */
import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  Swords, RefreshCw, ChevronRight, ChevronDown, Shield,
  ShieldAlert, ShieldCheck, Crosshair, Flame, Crown, Target, FileWarning,
} from 'lucide-react'
import { clsx } from 'clsx'
import { api } from '../lib/api'
import RelatedItems from '../components/RelatedItems'
import KpiTile from '../components/KpiTile'
import PageHeader from '../components/PageHeader'
import ErrorBanner from '../components/ErrorBanner'
import EmptyState from '../components/EmptyState'
import { RESIDUAL_BADGE } from '../lib/badges'

interface KillStep {
  step: string
  attck_id: string
  technique: string
  uses: string
  success_prob: number
}
interface DefStep {
  step?: string
  attck_id?: string
  i: number
  detected: boolean
  blocked: boolean
  gap: string | null
  recommended_control: string
}
interface Defense {
  per_step: DefStep[]
  steps_caught: number
  steps_missed: number
  residual_risk: string
  top_recommendation: string
  llm_evaluated?: boolean
}
interface Scenario {
  id: number
  objective: string
  overall_success_prob: number
  narrative: string
  kill_chain: KillStep[]
  defense: Defense
  residual_risk: string | null
  top_recommendation: string | null
  crown_jewels_at_risk?: string[]
}
interface Exercise {
  id: number
  ran_at: string | null
  objectives: string[]
  scenario_count: number
  red_win_rate: number
  green_catch_rate: number
  green_blind?: boolean | null
  gaps_filed: number
  summary: string | null
  status: string
}
interface SummaryKpi {
  last_red_win_rate: number | null
  last_green_catch_rate: number | null
  last_green_blind: boolean | null
  open_gaps_filed: number
  scenario_library: number
  exercises_run: number
  trend_direction: string
  last_exercise_at: string | null
}

const pct = (v: number | null | undefined) =>
  v == null ? '—' : `${Math.round(v * 100)}%`

/** Red-vs-green scoreboard: two opposing bars on a shared 0-100% axis. */
function Scoreboard({ redWin, greenCatch, blind }: { redWin: number | null; greenCatch: number | null; blind?: boolean | null }) {
  const r = redWin == null ? 0 : Math.max(0, Math.min(1, redWin))
  const g = greenCatch == null ? 0 : Math.max(0, Math.min(1, greenCatch))
  return (
    <div className="card p-4 space-y-3">
      <div className="text-[11px] uppercase tracking-widest text-text-muted flex items-center gap-1">
        <Swords size={13} /> Scoreboard
      </div>
      <div>
        <div className="flex justify-between text-[11px] mb-1">
          <span className="text-rose-light font-semibold flex items-center gap-1"><Crosshair size={11} /> RED win-rate</span>
          <span className="text-rose-light font-bold">{pct(redWin)}</span>
        </div>
        <div className="relative h-3 rounded bg-surface-raised overflow-hidden">
          <div className="absolute inset-y-0 left-0 rounded bg-rose/60" style={{ width: `${r * 100}%` }} />
        </div>
        {blind && (
          <div className="text-[10px] text-amber-light mt-1">
            unopposed attacker — defender has no detection coverage mapped
          </div>
        )}
      </div>
      <div>
        <div className="flex justify-between text-[11px] mb-1">
          <span className="text-emerald font-semibold flex items-center gap-1"><ShieldCheck size={11} /> GREEN catch-rate</span>
          <span className="text-emerald font-bold">{pct(greenCatch)}</span>
        </div>
        <div className="relative h-3 rounded bg-surface-raised overflow-hidden">
          <div className="absolute inset-y-0 left-0 rounded bg-emerald/60" style={{ width: `${g * 100}%` }} />
        </div>
        {blind && (
          <div className="text-[10px] text-amber-light mt-1">
            ⚠ no detection coverage — defender is blind (not tested-and-missed)
          </div>
        )}
      </div>
    </div>
  )
}

/** Trend sparkline over recent exercises (green catch-rate). */
function Sparkline({ points }: { points: { green_catch_rate: number }[] }) {
  if (!points.length) return <div className="text-[10px] text-text-muted">No trend yet.</div>
  const w = 160, h = 36
  const vals = points.map(p => p.green_catch_rate ?? 0)
  const max = 1, min = 0
  const step = points.length > 1 ? w / (points.length - 1) : w
  const path = vals.map((v, i) => {
    const x = i * step
    const y = h - ((v - min) / (max - min || 1)) * h
    return `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`
  }).join(' ')
  return (
    <svg width={w} height={h} className="overflow-visible">
      <path d={path} fill="none" stroke="currentColor" strokeWidth={1.5} className="text-emerald" />
      {vals.map((v, i) => (
        <circle key={i} cx={i * step} cy={h - ((v - min) / (max - min || 1)) * h} r={2} className="fill-emerald" />
      ))}
    </svg>
  )
}

function StepVerdict({ ks, ds }: { ks: KillStep; ds?: DefStep }) {
  const detected = ds?.detected
  const blocked = ds?.blocked
  const missed = ds && !detected && !blocked
  return (
    <div className="border border-border rounded p-2.5 space-y-1.5">
      <div className="flex items-start gap-2 flex-wrap">
        <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-surface-raised text-text-secondary">
          {ks.attck_id || 'T????'}
        </span>
        <span className="text-[12px] text-text-primary font-medium flex-1 min-w-[140px]">{ks.step}</span>
        <span className="text-[10px] text-text-muted">p={(ks.success_prob * 100).toFixed(0)}%</span>
      </div>
      {ks.technique && <div className="text-[10px] text-text-muted">ATT&CK: {ks.technique}</div>}
      {ks.uses && <div className="text-[10px] text-text-secondary">uses: <span className="text-text-primary">{ks.uses}</span></div>}
      <div className="flex items-center gap-2 flex-wrap text-[10px]">
        {blocked && <span className="inline-flex items-center gap-1 text-cyan"><Shield size={11} /> 🛡 blocked</span>}
        {detected && <span className="inline-flex items-center gap-1 text-emerald"><ShieldCheck size={11} /> ✓ detected</span>}
        {missed && <span className="inline-flex items-center gap-1 text-rose-light font-semibold"><ShieldAlert size={11} /> ✗ missed</span>}
      </div>
      {ds?.gap && <div className="text-[10px] text-rose-light">gap: {ds.gap}</div>}
      {ds?.recommended_control && (
        <div className="text-[10px] text-text-secondary">→ recommend: <span className="text-text-primary">{ds.recommended_control}</span></div>
      )}
    </div>
  )
}

export default function PurpleRangePage() {
  const [kpi, setKpi] = useState<SummaryKpi | null>(null)
  const [exercises, setExercises] = useState<Exercise[]>([])
  const [status, setStatus] = useState<{ trend?: { green_catch_rate: number }[]; estate?: Record<string, number>; range_loaded?: boolean } | null>(null)
  const [selected, setSelected] = useState<number | null>(null)
  const [detail, setDetail] = useState<{ exercise: Exercise; scenarios: Scenario[] } | null>(null)
  const [openScenario, setOpenScenario] = useState<Set<number>>(new Set())
  const [loading, setLoading] = useState(false)
  const [running, setRunning] = useState(false)
  const [error, setError] = useState('')
  const [msg, setMsg] = useState('')

  const load = useCallback(async () => {
    setLoading(true); setError('')
    try {
      const [s, ex, st] = await Promise.all([
        api.get('/api/purple-range/summary'),
        api.get('/api/purple-range/exercises', { params: { limit: 25 } }),
        api.get('/api/purple-range/status'),
      ])
      setKpi(s.data as SummaryKpi)
      setExercises((ex.data.items || []) as Exercise[])
      setStatus(st.data)
    } catch (e: unknown) {
      setError((e as { message?: string })?.message || 'failed to load')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { void load() }, [load])

  const openExercise = async (id: number) => {
    setSelected(id); setDetail(null)
    try {
      const r = await api.get(`/api/purple-range/exercises/${id}`)
      setDetail(r.data)
    } catch {
      setError('failed to load exercise detail')
    }
  }

  const run = async () => {
    setRunning(true); setMsg(''); setError('')
    try {
      const r = await api.post('/api/purple-range/run')
      const d = r.data as { scenario_count: number; red_win_rate: number; green_catch_rate: number; gaps_filed: number; status: string }
      setMsg(`Exercise complete — ${d.scenario_count} scenario(s), RED ${pct(d.red_win_rate)} / GREEN ${pct(d.green_catch_rate)}, ${d.gaps_filed} gap(s) filed.`)
      await load()
    } catch (e: unknown) {
      const code = (e as { response?: { status?: number } })?.response?.status
      setError(code === 401 ? 'Admin role required to run an exercise.' : 'Run failed.')
    } finally {
      setRunning(false)
    }
  }

  const toggleScenario = (id: number) =>
    setOpenScenario(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id); else next.add(id)
      return next
    })

  const empty = exercises.length === 0

  return (
    <div className="space-y-5 animate-fade-in">
      <PageHeader
        icon={Swords} iconClassName="text-purple-light" title="PURPLE RANGE"
        subtitle="continuous attack/defend simulation over the modeled estate"
        backTo="/app/detection-coverage" backLabel="Detection Coverage"
        actions={<>
          {msg && <span className="text-[10px] text-emerald max-w-[280px] truncate" title={msg}>{msg}</span>}
          <button type="button" onClick={() => void run()} disabled={running} className="btn-ghost">
            {running ? <RefreshCw size={11} className="animate-spin" /> : <Crosshair size={11} />}
            {running ? 'Running…' : 'Run exercise'}
          </button>
          <button type="button" onClick={() => void load()} disabled={loading} className="btn-ghost">
            <RefreshCw size={11} className={loading ? 'animate-spin' : ''} /> Refresh
          </button>
        </>}
      />

      <div className="card p-3 text-[12px] text-text-secondary leading-relaxed">
        <span className="text-text-primary font-medium">Breach-and-attack simulation (BAS).</span>{' '}
        A RED agent reasons as an adversary over our modeled estate (toxic-combo paths + losing
        exploitation races + identity/network weaknesses) and builds kill-chains; a GREEN agent
        judges each step against our detection coverage and compensating controls; a PURPLE agent
        referees, scores, and files the unmitigated gaps to the review queue.
        <div className="mt-1.5 text-[10px] text-amber-light font-medium">
          Simulation over the modeled estate · no live exploitation · gaps go to the review queue (never auto-applied).
        </div>
      </div>

      <ErrorBanner message={error} />

      {/* Defender-blind banner — distinguishes "no detection coverage mapped"
          from "tested and missed". Scores then read as an unopposed attacker. */}
      {kpi?.last_green_blind && (
        <div className="card p-3 border border-amber/40 bg-amber/5 text-[12px] text-amber-light flex items-start gap-2">
          <ShieldAlert size={15} className="mt-0.5 shrink-0" />
          <span>
            <span className="font-semibold">Defender blind</span> — no detection coverage mapped for these
            techniques. Scores reflect an <span className="font-semibold">unopposed attacker</span>: the GREEN
            catch-rate is 0% because the defender has nothing to catch with, not because it was tested and lost.
            The RED win-rate is the honest "we'd lose this" reading until detection coverage is configured.
          </span>
        </div>
      )}

      {/* KPI tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiTile label="RED win-rate" value={pct(kpi?.last_red_win_rate)} tone="text-rose-light"
              hint="scenarios attacker wins" />
        <KpiTile label="GREEN catch-rate" value={pct(kpi?.last_green_catch_rate)}
              tone={kpi?.last_green_blind ? 'text-amber-light' : 'text-emerald'}
              hint={kpi?.last_green_blind ? '⚠ blind — no coverage mapped' : 'kill-chain steps caught'} />
        <KpiTile label="Open gaps filed" value={kpi?.open_gaps_filed ?? '—'} tone="text-amber-light"
              hint="→ Phase-A review queue" />
        <KpiTile label="Scenarios in library" value={kpi?.scenario_library ?? '—'}
              hint={`${kpi?.exercises_run ?? 0} exercise(s) run`} />
      </div>

      {/* Scoreboard + trend */}
      <div className="grid md:grid-cols-2 gap-3">
        <Scoreboard redWin={kpi?.last_red_win_rate ?? null} greenCatch={kpi?.last_green_catch_rate ?? null} blind={kpi?.last_green_blind} />
        <div className="card p-4 space-y-2">
          <div className="text-[11px] uppercase tracking-widest text-text-muted">GREEN catch-rate trend</div>
          <Sparkline points={status?.trend || []} />
          <div className="text-[10px] text-text-muted">
            Trend: <span className="text-text-secondary">{kpi?.trend_direction || 'flat'}</span>
            {status?.range_loaded && status.estate && (
              <span className="ml-2">· Range: {status.estate.total_assets ?? 0} assets, {status.estate.kev_count ?? 0} KEV, {status.estate.crown_jewels ?? 0} crown jewels</span>
            )}
          </div>
        </div>
      </div>

      {/* Exercise list + detail */}
      <div className="grid lg:grid-cols-3 gap-3">
        <div className="space-y-2">
          <div className="text-[11px] uppercase tracking-widest text-text-muted">Exercises</div>
          {empty && (
            <EmptyState variant="inline" icon={Swords} tone="text-purple-light"
              title="No exercises yet — run one"
              hint="Exercises reason over the modeled estate; nothing is executed against real systems." />
          )}
          {exercises.map(ex => (
            <button key={ex.id} type="button" onClick={() => void openExercise(ex.id)}
                    className={clsx('card p-3 w-full text-left hover:border-purple-400/40 transition',
                                    selected === ex.id && 'border-purple-400/60')}>
              <div className="flex items-center justify-between">
                <span className="text-[12px] text-text-primary font-medium">
                  {ex.ran_at ? new Date(ex.ran_at).toLocaleString() : `#${ex.id}`}
                </span>
                <ChevronRight size={13} className="text-text-muted" />
              </div>
              <div className="flex items-center gap-3 mt-1.5 text-[10px]">
                <span className="text-rose-light">RED {pct(ex.red_win_rate)}</span>
                <span className="text-emerald">GREEN {pct(ex.green_catch_rate)}</span>
                <span className="text-text-muted">{ex.scenario_count} scn</span>
                {ex.gaps_filed > 0 && <span className="text-amber-light inline-flex items-center gap-0.5"><FileWarning size={10} /> {ex.gaps_filed}</span>}
              </div>
            </button>
          ))}
        </div>

        {/* Scenario detail */}
        <div className="lg:col-span-2 space-y-2">
          {!detail && selected && <div className="card p-4 text-[12px] text-text-muted">Loading exercise…</div>}
          {detail && (
            <>
              {detail.exercise.summary && (
                <div className="card p-3 text-[11px] text-text-secondary">{detail.exercise.summary}</div>
              )}
              {detail.scenarios.length === 0 && (
                <div className="card p-4 text-[12px] text-text-muted">
                  No scenarios — the modeled estate was too sparse or the LLM was unavailable.
                  The attack/defend loop still ran end-to-end. (Simulation — nothing executed.)
                </div>
              )}
              {detail.scenarios.map(sc => {
                const open = openScenario.has(sc.id)
                const def = sc.defense || ({} as Defense)
                const byIdx = new Map((def.per_step || []).map(p => [p.i, p]))
                return (
                  <div key={sc.id} className="card p-3 space-y-2">
                    <button type="button" onClick={() => toggleScenario(sc.id)} className="w-full text-left flex items-start gap-2">
                      {open ? <ChevronDown size={14} className="mt-0.5 text-text-muted" /> : <ChevronRight size={14} className="mt-0.5 text-text-muted" />}
                      <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <Target size={13} className="text-rose-light" />
                          <span className="text-[13px] text-text-primary font-semibold">{sc.objective}</span>
                          <span className={clsx('text-[9px] px-1.5 py-0.5 rounded border', RESIDUAL_BADGE[(sc.residual_risk || 'medium')] || RESIDUAL_BADGE.medium)}>
                            {sc.residual_risk || 'medium'} residual
                          </span>
                        </div>
                        <div className="flex items-center gap-3 mt-1 text-[10px]">
                          <span className="text-rose-light">attack success {pct(sc.overall_success_prob)}</span>
                          <span className="text-emerald">{def.steps_caught ?? 0}/{(def.steps_caught ?? 0) + (def.steps_missed ?? 0)} caught</span>
                        </div>
                      </div>
                    </button>

                    {open && (
                      <div className="space-y-2 pl-1">
                        {sc.narrative && <div className="text-[11px] text-text-secondary italic">{sc.narrative}</div>}
                        {(sc.crown_jewels_at_risk?.length || 0) > 0 && (
                          <div className="text-[10px] text-amber-light inline-flex items-center gap-1">
                            <Crown size={11} /> Crown jewels at risk: {sc.crown_jewels_at_risk!.join(', ')}
                          </div>
                        )}
                        <div className="space-y-1.5">
                          {sc.kill_chain.map((ks, i) => (
                            <StepVerdict key={i} ks={ks} ds={byIdx.get(i)} />
                          ))}
                        </div>
                        {(sc.top_recommendation || def.top_recommendation) && (
                          <div className="border border-emerald/30 bg-emerald/5 rounded p-2 text-[11px] text-emerald inline-flex items-start gap-1.5">
                            <Flame size={12} className="mt-0.5" />
                            <span><span className="font-semibold">Top recommendation:</span> {sc.top_recommendation || def.top_recommendation}</span>
                          </div>
                        )}
                        {detail.exercise.gaps_filed > 0 && (
                          <Link to="/app/cdm/changes" className="text-[10px] text-cyan hover:underline inline-flex items-center gap-1">
                            <FileWarning size={11} /> View filed gaps in the change-proposal review queue →
                          </Link>
                        )}
                        <RelatedItems entityType="purple_scenario" entityKey={String(sc.id)} />
                      </div>
                    )}
                  </div>
                )
              })}
            </>
          )}
          {!detail && !selected && !empty && (
            <div className="card p-4 text-[12px] text-text-muted">Select an exercise to see the RED kill-chains and GREEN verdicts.</div>
          )}
        </div>
      </div>
    </div>
  )
}
